"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef, useState } from "react";
import { RiAddLine, RiCheckLine, RiCloseLine, RiPencilLine } from "react-icons/ri";
import { useParams, useRouter } from "next/navigation";
import { renderTextWithLinks } from "@/app/[tenant]/(console)/comms/chat/window/message/shared";
import ProviderAvatar from "@/components/provider-avatar";
import { MessageAttachments } from "@/components/team-chat/message-attachments";
import { QuickEmojiPicker } from "@/components/ui/emoji-picker";
import { useUser } from "@/contexts/user-provider";
import { formatTime } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import type { TeamMessageSummary } from "@/types/team-chat";
import { useAddReaction, useEditTeamMessage, useRemoveReaction } from "@/query/team-chat";
import { useTeamChatDrawerStore } from "@/stores/team-chat-store";

/** Regex matching entity mentions: {id|label} or {id} (id must contain _ to skip template vars) */
const MENTION_REGEX = /\{([a-z]+_[a-zA-Z0-9]+)(?:\|([^}]+))?\}/g;
const MAX_READ_BY_NAMES = 3;
const MAX_READ_BY_CHARS = 35;
function formatReadByNames(readBy: string[], memberNameMap: Map<string, string>): string {
  const names = readBy.map(pid => (memberNameMap.get(pid) ?? pid).split(/\s+/)[0] || pid);
  if (names.length === 0) return "";
  const suffix = " and others";
  let taken: string[] = [];
  for (const name of names) {
    if (taken.length >= MAX_READ_BY_NAMES) {
      return `${taken.join(", ")}${suffix}`;
    }
    const next = taken.length === 0 ? name : `${taken.join(", ")}, ${name}`;
    if (next.length > MAX_READ_BY_CHARS && taken.length > 0) {
      return `${taken.join(", ")}${suffix}`;
    }
    taken = taken.length === 0 ? [name] : [...taken, name];
  }
  return taken.join(", ");
}

/**
 * Convert raw stored content to a user-friendly edit string and build
 * a reverse map so we can reconstruct entity references on save.
 * {pat_abc123|John Doe} → @John Doe   (reverse: "@John Doe" → "{pat_abc123|John Doe}")
 * {usr_abc123}          → @DisplayName (via memberNameMap)
 */
function mentionsToEditText(content: string, memberNameMap: Map<string, string>): {
  text: string;
  reverseMap: Map<string, string>;
} {
  const reverseMap = new Map<string, string>();
  const text = content.replace(MENTION_REGEX, (match, id: string, label?: string) => {
    const name = label || memberNameMap.get(id) || id;
    const display = `@${name}`;
    reverseMap.set(display, match);
    return display;
  });
  return {
    text,
    reverseMap
  };
}

/**
 * Convert user-edited text back to stored format by replacing @Name with {id|label}.
 */
function editTextToMentions(text: string, reverseMap: Map<string, string>): string {
  let result = text;
  // Replace longest matches first to avoid partial replacements
  const entries = Array.from(reverseMap.entries()).sort((a, b) => b[0].length - a[0].length);
  for (const [display, raw] of entries) {
    result = result.replaceAll(display, raw);
  }
  return result;
}

/**
 * Renders message content with mentions as inline text.
 * Patient mentions are clickable via data-patient-id for event delegation.
 * Provider mentions are styled inline text without patient navigation.
 */
function renderContentWithMentions(content: string, isOwn: boolean, memberNameMap: Map<string, string>): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let keyCounter = 0;
  const regex = new RegExp(MENTION_REGEX.source, "g");
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      const textBefore = content.slice(lastIndex, match.index);
      parts.push(<React.Fragment key={`text-${keyCounter++}`}>
          {renderTextWithLinks(textBefore, {
          isOwnMessage: isOwn
        })}
        </React.Fragment>);
    }
    const entityId = match[1];
    const displayName = match[2] || memberNameMap.get(entityId) || entityId;
    const isPatientMention = entityId.startsWith("pat_");
    parts.push(<span key={`mention-${keyCounter++}`} data-patient-id={isPatientMention ? entityId : undefined} className={cx("font-semibold", isPatientMention && "cursor-pointer hover:opacity-80", isOwn ? "text-primary-foreground" : "text-primary")}>
        {displayName}
      </span>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < content.length) {
    const remaining = content.slice(lastIndex);
    parts.push(<React.Fragment key={`text-${keyCounter++}`}>
        {renderTextWithLinks(remaining, {
        isOwnMessage: isOwn
      })}
      </React.Fragment>);
  }
  if (parts.length === 0) {
    return renderTextWithLinks(content, {
      isOwnMessage: isOwn
    });
  }
  return parts;
}
interface MessageBubbleProps {
  message: TeamMessageSummary;
  showSender: boolean;
  showAvatar: boolean;
  /** Maps entity IDs (e.g. usr_xxx) to display names for mention resolution */
  memberNameMap: Map<string, string>;
}
export function MessageBubble(t0) {
  const $ = _c(97);
  const {
    message,
    showSender,
    showAvatar,
    memberNameMap
  } = t0;
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useParams();
  const router = useRouter();
  const closeDrawer = useTeamChatDrawerStore(_temp);
  const isOwn = currentUser?.id === message.sender.providerId;
  const isPending = (message as TeamMessageSummary & {
    _pending?: boolean;
  })._pending;
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const mentionReverseMapRef = useRef(t1);
  const editMutation = useEditTeamMessage(message.conversationId);
  const addReactionMutation = useAddReaction(message.conversationId);
  const removeReactionMutation = useRemoveReaction(message.conversationId);
  let t2;
  if ($[1] !== message.createdDate) {
    t2 = formatTime(message.createdDate);
    $[1] = message.createdDate;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const timestamp = t2;
  if (message.isDeleted) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex justify-center py-1"><p className="text-caption italic text-foreground-muted">This message was deleted</p></div>;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  const hasContent = !!message.content;
  let t3;
  if ($[4] !== memberNameMap || $[5] !== message.content) {
    t3 = () => {
      const {
        text,
        reverseMap
      } = mentionsToEditText(message.content ?? "", memberNameMap);
      mentionReverseMapRef.current = reverseMap;
      setEditContent(text);
      setIsEditing(true);
      setTimeout(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.focus();
          textarea.style.height = "auto";
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
      }, 0);
    };
    $[4] = memberNameMap;
    $[5] = message.content;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleStartEdit = t3;
  let t4;
  if ($[7] !== editContent || $[8] !== editMutation || $[9] !== message.content || $[10] !== message.id) {
    t4 = () => {
      const trimmed = editContent.trim();
      const withMentions = editTextToMentions(trimmed, mentionReverseMapRef.current);
      if (!withMentions || withMentions === message.content) {
        setIsEditing(false);
        return;
      }
      editMutation.mutate({
        messageId: message.id,
        content: withMentions
      }, {
        onSettled: () => setIsEditing(false)
      });
    };
    $[7] = editContent;
    $[8] = editMutation;
    $[9] = message.content;
    $[10] = message.id;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleSaveEdit = t4;
  let t5;
  if ($[12] !== handleSaveEdit) {
    t5 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSaveEdit();
      }
      if (e.key === "Escape") {
        setIsEditing(false);
      }
    };
    $[12] = handleSaveEdit;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleEditKeyDown = t5;
  let t6;
  if ($[14] !== addReactionMutation || $[15] !== message.id || $[16] !== message.reactions || $[17] !== removeReactionMutation) {
    t6 = emoji => {
      const existing = message.reactions?.find(r => r.emoji === emoji);
      if (existing?.reactedByMe) {
        removeReactionMutation.mutate({
          messageId: message.id,
          emoji
        });
      } else {
        addReactionMutation.mutate({
          messageId: message.id,
          emoji
        });
      }
    };
    $[14] = addReactionMutation;
    $[15] = message.id;
    $[16] = message.reactions;
    $[17] = removeReactionMutation;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const handleReactionToggle = t6;
  const t7 = isOwn ? "flex-row-reverse" : "flex-row";
  const t8 = showSender ? "mt-3" : "mt-0.5";
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = cx("group/row flex gap-2", t7, t8);
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== isOwn || $[23] !== message.sender.providerId || $[24] !== showAvatar) {
    t10 = !isOwn && <div className="w-8 shrink-0 self-end">{showAvatar && <ProviderAvatar providerId={message.sender.providerId} size={32} />}</div>;
    $[22] = isOwn;
    $[23] = message.sender.providerId;
    $[24] = showAvatar;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const t11 = isOwn ? "items-end" : "items-start";
  let t12;
  if ($[26] !== t11) {
    t12 = cx("flex min-w-0 max-w-[70%] flex-col", t11);
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== isOwn || $[29] !== message.sender.firstName || $[30] !== message.sender.lastName || $[31] !== showSender) {
    t13 = showSender && !isOwn && <p className="mb-0.5 ml-1 text-caption-strong text-primary">{message.sender.firstName} {message.sender.lastName}</p>;
    $[28] = isOwn;
    $[29] = message.sender.firstName;
    $[30] = message.sender.lastName;
    $[31] = showSender;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== handleStartEdit || $[34] !== hasContent || $[35] !== isEditing || $[36] !== isOwn || $[37] !== isPending) {
    t14 = !isEditing && !isPending && <div className={cx("absolute -top-3 z-10 hidden items-center gap-0.5 rounded-lg border border-border bg-background px-1 py-0.5 shadow-sm group-hover/bubble:flex", isOwn ? "right-1" : "left-1")}><button onClick={() => setShowEmojiPicker(_temp2)} className="rounded p-0.5 text-foreground-secondary hover:text-foreground" title="Add reaction"><RiAddLine className="h-3.5 w-3.5" /></button>{isOwn && hasContent && <button onClick={handleStartEdit} className="rounded p-0.5 text-foreground-secondary hover:text-foreground"><RiPencilLine className="h-3.5 w-3.5" /></button>}</div>;
    $[33] = handleStartEdit;
    $[34] = hasContent;
    $[35] = isEditing;
    $[36] = isOwn;
    $[37] = isPending;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== handleReactionToggle || $[40] !== isOwn || $[41] !== showEmojiPicker) {
    t15 = showEmojiPicker && <QuickEmojiPicker onSelect={emoji_0 => handleReactionToggle(emoji_0)} onClose={() => setShowEmojiPicker(false)} alignRight={isOwn} className="-top-10" />;
    $[39] = handleReactionToggle;
    $[40] = isOwn;
    $[41] = showEmojiPicker;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const t16 = isOwn ? "rounded-2xl rounded-br-sm bg-primary text-primary-foreground" : "rounded-2xl rounded-bl-sm bg-background-secondary text-foreground";
  let t17;
  if ($[43] !== t16) {
    t17 = cx("overflow-hidden px-2.5 py-1.5 shadow-sm", t16);
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== closeDrawer || $[46] !== router || $[47] !== tenant) {
    t18 = e_0 => {
      const el = (e_0.target as HTMLElement).closest("[data-patient-id]");
      if (el?.dataset.patientId) {
        closeDrawer();
        router.push(`/${tenant}/patient/${el.dataset.patientId}`);
      }
    };
    $[45] = closeDrawer;
    $[46] = router;
    $[47] = tenant;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  const t19 = message.files;
  const t20 = hasContent && "mb-1.5";
  let t21;
  if ($[49] !== t20) {
    t21 = cx(t20);
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== isOwn || $[52] !== message.files || $[53] !== t21) {
    t22 = <MessageAttachments files={t19} isOwn={isOwn} className={t21} />;
    $[51] = isOwn;
    $[52] = message.files;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== editContent || $[56] !== handleEditKeyDown || $[57] !== handleSaveEdit || $[58] !== hasContent || $[59] !== isEditing || $[60] !== isOwn || $[61] !== isPending || $[62] !== memberNameMap || $[63] !== message || $[64] !== timestamp) {
    t23 = isEditing ? <div><textarea ref={textareaRef} value={editContent} onChange={e_1 => {
        setEditContent(e_1.target.value);
        e_1.target.style.height = "auto";
        e_1.target.style.height = `${e_1.target.scrollHeight}px`;
      }} onKeyDown={handleEditKeyDown} className={cx("w-full resize-none border-0 bg-transparent p-0 text-body shadow-none outline-none ring-0 focus:border-0 focus:shadow-none focus:outline-none focus:ring-0", isOwn ? "text-primary-foreground" : "text-foreground")} style={{
        outline: "none",
        boxShadow: "none"
      }} rows={1} /><div className="mt-1 flex items-center justify-end gap-1"><button onClick={() => setIsEditing(false)} className={cx("flex h-5 w-5 items-center justify-center rounded-full transition-colors", isOwn ? "bg-primary-foreground/15 text-primary-foreground hover:bg-primary-foreground/25" : "bg-foreground/10 text-foreground-secondary hover:bg-foreground/15")}><RiCloseLine className="h-3 w-3" /></button><button onClick={handleSaveEdit} className={cx("flex h-5 w-5 items-center justify-center rounded-full transition-colors", isOwn ? "bg-primary-foreground/15 text-primary-foreground hover:bg-primary-foreground/25" : "bg-primary/15 text-primary hover:bg-primary/25")}><RiCheckLine className="h-3 w-3" /></button></div></div> : hasContent && <p className="whitespace-pre-wrap break-words text-body text-inherit [overflow-wrap:anywhere]">{renderContentWithMentions(message.content, isOwn, memberNameMap)}<span className={cx("ml-2 inline-block align-bottom text-tiny leading-[18px]", isOwn ? "text-primary-foreground/60" : "text-foreground-muted")}>{timestamp}{message.editedAt && <span>{" \xB7 edited"}</span>}<span>{isOwn && !isPending && (() => {
            const readBy = (message as TeamMessageSummary & {
              readBy?: string[];
            }).readBy ?? [];
            if (!readBy.length) {
              return "";
            }
            return ` · Seen by ${formatReadByNames(readBy, memberNameMap)}`;
          })()}</span></span></p>;
    $[55] = editContent;
    $[56] = handleEditKeyDown;
    $[57] = handleSaveEdit;
    $[58] = hasContent;
    $[59] = isEditing;
    $[60] = isOwn;
    $[61] = isPending;
    $[62] = memberNameMap;
    $[63] = message;
    $[64] = timestamp;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== hasContent || $[67] !== isEditing || $[68] !== isOwn || $[69] !== isPending || $[70] !== memberNameMap || $[71] !== message || $[72] !== timestamp) {
    t24 = !hasContent && !isEditing && <p className={cx("mt-1 text-right text-tiny", isOwn ? "text-primary-foreground/70" : "text-foreground-muted")}>{timestamp}{message.editedAt && <span>{" \xB7 edited"}</span>}<span>{isOwn && !isPending && (() => {
          const readBy_0 = (message as TeamMessageSummary & {
            readBy?: string[];
          }).readBy ?? [];
          if (!readBy_0.length) {
            return "";
          }
          return ` · Seen by ${formatReadByNames(readBy_0, memberNameMap)}`;
        })()}</span></p>;
    $[66] = hasContent;
    $[67] = isEditing;
    $[68] = isOwn;
    $[69] = isPending;
    $[70] = memberNameMap;
    $[71] = message;
    $[72] = timestamp;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== t17 || $[75] !== t18 || $[76] !== t22 || $[77] !== t23 || $[78] !== t24) {
    t25 = <div className={t17} onClick={t18}>{t22}{t23}{t24}</div>;
    $[74] = t17;
    $[75] = t18;
    $[76] = t22;
    $[77] = t23;
    $[78] = t24;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== handleReactionToggle || $[81] !== isOwn || $[82] !== message.reactions) {
    t26 = message.reactions && message.reactions.length > 0 && <div className={cx("mt-1 flex flex-wrap gap-1", isOwn ? "justify-end" : "justify-start")}>{message.reactions.map(reaction => <button key={reaction.emoji} onClick={() => handleReactionToggle(reaction.emoji)} className={cx("flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-caption transition-colors", reaction.reactedByMe ? "border-primary/40 bg-primary/10 text-foreground" : "border-border bg-background-secondary text-foreground-secondary hover:bg-hover")}><span>{reaction.emoji}</span><span className="text-tiny">{reaction.count}</span></button>)}</div>;
    $[80] = handleReactionToggle;
    $[81] = isOwn;
    $[82] = message.reactions;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  let t27;
  if ($[84] !== t14 || $[85] !== t15 || $[86] !== t25 || $[87] !== t26) {
    t27 = <div className="group/bubble relative max-w-full">{t14}{t15}{t25}{t26}</div>;
    $[84] = t14;
    $[85] = t15;
    $[86] = t25;
    $[87] = t26;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  let t28;
  if ($[89] !== t12 || $[90] !== t13 || $[91] !== t27) {
    t28 = <div className={t12}>{t13}{t27}</div>;
    $[89] = t12;
    $[90] = t13;
    $[91] = t27;
    $[92] = t28;
  } else {
    t28 = $[92];
  }
  let t29;
  if ($[93] !== t10 || $[94] !== t28 || $[95] !== t9) {
    t29 = <div className={t9}>{t10}{t28}</div>;
    $[93] = t10;
    $[94] = t28;
    $[95] = t9;
    $[96] = t29;
  } else {
    t29 = $[96];
  }
  return t29;
}
function _temp2(v) {
  return !v;
}
function _temp(s) {
  return s.closeDrawer;
}